/**
 * Collapsible "Record installment payment" form on the loan detail page.
 *
 * Lets an operator with `payment.post_manual` book a cash, bank-transfer,
 * or out-of-band M-Pesa INSTALLMENT against a loan that has already gone
 * live. Reuses the same settle pipeline as the M-Pesa webhook, so the
 * form does NOT need to wire any follow-up:
 *   - LOCKED / IN_ARREARS loan + payment -> reverse auto-cascade
 *     (Unlock / ClearArrears) fires via `system.LoanPaymentSettled`.
 *
 * Deposit recording for PENDING_DEPOSIT loans lives in a sibling
 * `RecordDepositForm` component — deposits have a fixed-amount rule
 * the server enforces (DepositAmountMismatch) and a different UX
 * (locked amount, contract-required guard).
 *
 * Self-gates on `payment.post_manual` — invisible to roles that don't
 * have it. Server enforces the same permission.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ManualPaymentChannel, ManualPaymentRequest, SettleResult } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { generateCashReference, isGeneratedCashReference } from "../lib/payment-reference.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";

interface Props {
  readonly loanId: string;
  readonly loanStatus: string;
  /** Called after a successful post so the parent can refresh its loan view. */
  readonly onPosted?: (result: SettleResult) => void;
}

const CHANNEL_LABELS: Record<ManualPaymentChannel, string> = {
  manual_bank_transfer: "Bank transfer",
  manual_cash: "Cash receipt",
  manual_mobile_money_other: "Mobile money (out-of-band)",
  manual_correction: "Correction / suspense",
};

const CHANNEL_OPTIONS: readonly ManualPaymentChannel[] = [
  "manual_bank_transfer",
  "manual_cash",
  "manual_mobile_money_other",
  "manual_correction",
];

const TERMINAL_LOAN_STATUSES: ReadonlySet<string> = new Set([
  "PAID_OFF",
  "WRITTEN_OFF",
  "REJECTED",
]);

/**
 * Statuses where an installment payment makes sense — the loan is
 * actually live (or in recovery). DRAFT / PENDING_VERIFICATION /
 * PENDING_DEPOSIT are pre-funding states where an installment payment
 * shouldn't exist (the deposit form covers PENDING_DEPOSIT). Hiding
 * here keeps operators from posting installments against half-set-up
 * loans where the schedule isn't yet authoritative.
 */
const INSTALLMENT_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set([
  "ACTIVE",
  "IN_ARREARS",
  "LOCKED",
  "IN_DISPUTE",
  "PENDING_RELEASE",
]);

/** Cash-side accounts the operator can post a receipt into (UPGRADE-01). */
interface CashAccountOption {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly category: string;
  readonly isActive: boolean;
}
const CASH_CATEGORIES: ReadonlySet<string> = new Set(["cash_on_hand", "bank", "mobile_money"]);

export function RecordManualPaymentForm(props: Props): JSX.Element | null {
  const auth = useAuth();
  const canPost = (auth.user?.permissions ?? []).includes("payment.post_manual");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const cfg = useCountryConfig(open);

  const [amountMinor, setAmountMinor] = useState("");
  const [channel, setChannel] = useState<ManualPaymentChannel | "">("");
  const [providerReference, setProviderReference] = useState("");
  const [note, setNote] = useState("");
  const [cashAccountId, setCashAccountId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successFlash, setSuccessFlash] = useState<string | null>(null);

  // Optional destination-account override — only offered to operators who can
  // read the ledger; everyone else posts to the channel default.
  const canReadLedger = (auth.user?.permissions ?? []).includes("ledger.read");
  const accountsQuery = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => api.get<readonly CashAccountOption[]>("/api/accounting/accounts"),
    enabled: open && canReadLedger,
  });
  const cashAccounts = (accountsQuery.data ?? []).filter(
    (a) => a.isActive && CASH_CATEGORIES.has(a.category),
  );

  const mutation = useMutation({
    mutationFn: (body: ManualPaymentRequest) =>
      api.post<SettleResult>("/api/payments/manual", body),
    onSuccess: (result) => {
      reset();
      setOpen(false);
      // Make the user-visible signal as specific as possible — they typed
      // a reference + amount, surface exactly that back to confirm the
      // server accepted it.
      setSuccessFlash(
        result.idempotent
          ? `Already recorded (${result.payment.providerReference}). No double-charge.`
          : `Payment posted — ${result.payment.providerReference}` +
              (result.loanAdvanced ? ", loan advanced to ACTIVE." : "."),
      );
      // Bust caches the loan detail page reads.
      void qc.invalidateQueries({ queryKey: ["loans", "byId", props.loanId] });
      void qc.invalidateQueries({ queryKey: ["loans", "list"] });
      void qc.invalidateQueries({ queryKey: ["payments", "list"] });
      props.onPosted?.(result);
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setAmountMinor("");
    setChannel("");
    setProviderReference("");
    setNote("");
    setCashAccountId("");
    setError(null);
  }

  /**
   * Cash receipts have no external reference, so selecting "Cash receipt"
   * auto-fills a generated `CASH-…` ref (the operator can still overtype a
   * physical receipt no.). Switching AWAY from cash clears an auto-generated
   * ref so a real one is entered; a hand-typed ref is left untouched.
   */
  function changeChannel(next: ManualPaymentChannel | ""): void {
    setChannel(next);
    setProviderReference((prev) => {
      if (next === "manual_cash") return prev.length === 0 ? generateCashReference() : prev;
      return isGeneratedCashReference(prev) ? "" : prev;
    });
  }

  function submit(): void {
    setError(null);
    const amount = Number(amountMinor);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError("Amount must be a positive whole number (minor units).");
      return;
    }
    if (!channel) {
      setError("Channel is required.");
      return;
    }
    const ref = providerReference.trim();
    if (ref.length < 4) {
      setError("Reference must be at least 4 characters (bank ref / receipt no.).");
      return;
    }
    const trimmedNote = note.trim();
    if (trimmedNote.length > 0 && trimmedNote.length < 3) {
      setError("Note must be at least 3 characters (or leave blank).");
      return;
    }
    if (!cfg.data) {
      setError("Country config not loaded yet — wait a moment.");
      return;
    }
    mutation.mutate({
      loanId: props.loanId,
      amount: { amountMinor: amount, currency: cfg.data.currency },
      channel,
      providerReference: ref,
      ...(trimmedNote.length > 0 ? { note: trimmedNote } : {}),
      ...(cashAccountId ? { cashAccountId } : {}),
    });
  }

  if (!canPost) return null;
  if (TERMINAL_LOAN_STATUSES.has(props.loanStatus)) return null;
  // Tightened gating: installment payments only against live loans.
  // DRAFT / PENDING_VERIFICATION / PENDING_DEPOSIT are pre-funding
  // states (PENDING_DEPOSIT uses the sibling RecordDepositForm, the
  // others shouldn't take a payment at all).
  if (!INSTALLMENT_ELIGIBLE_STATUSES.has(props.loanStatus)) return null;

  if (!open) {
    return (
      <div className="space-y-2">
        {successFlash ? (
          <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">
            {successFlash}
          </div>
        ) : null}
        <Button onClick={() => setOpen(true)}>Record installment payment</Button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Record installment payment</p>
      <p className="text-xs text-slate-500">
        Installment posting for an ACTIVE / LOCKED / IN_ARREARS loan — cash, bank transfer, or
        out-of-band mobile money. Idempotent on (channel, reference) — re-submitting the same
        reference is safe.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount" hint={`minor units${cfg.data ? ` (${cfg.data.currency})` : ""}`}>
          <TextInput
            inputMode="numeric"
            value={amountMinor}
            onChange={(e) => setAmountMinor(e.target.value)}
          />
        </Field>
        <Field label="Channel">
          <Select
            value={channel}
            onChange={(e) => changeChannel(e.target.value as ManualPaymentChannel | "")}
          >
            <option value="">Select channel…</option>
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {canReadLedger && cashAccounts.length > 0 ? (
        <Field
          label="Destination account"
          hint="optional — defaults to the channel's account (e.g. cash → Main Cash)"
        >
          <Select value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
            <option value="">Channel default</option>
            {cashAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      <Field
        label="Reference"
        hint={
          channel === "manual_cash"
            ? "auto-generated for cash — or overtype the physical receipt no."
            : "bank ref, cash receipt #, M-Pesa tx id, etc — ≥ 4 chars"
        }
      >
        <TextInput
          value={providerReference}
          onChange={(e) => setProviderReference(e.target.value)}
        />
      </Field>

      <Field label="Note" hint="optional — why are you posting manually? lands on the audit row">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Posting…" : "Post payment"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
